import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  LinkOutlined,
  MoreOutlined,
  StarOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DragOutlined,
  DeleteOutlined,
  ExportOutlined,
  StarFilled,
  FontSizeOutlined,
  NumberOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  EyeInvisibleOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import { Button, Input, Dropdown, Switch, Modal } from 'antd';
import './Database.css';
import SkeletonWithLoaders from 'components/common/Spinner';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

import { ModuleRegistry } from 'ag-grid-community';
import {
  ClientSideRowModelModule,
  TextEditorModule,
  SelectEditorModule,
  NumberEditorModule,
  PinnedRowModule,
  ColumnApiModule,
  QuickFilterModule,
  NumberFilterModule,
} from 'ag-grid-community';
import FilterPanel from './FilterPanel';
import VersionPanel from '../versionHistory/VersionPanel';
import ShareItemPermision from '../share/ShareItemPermision';
import MoveModal from '../Sidebar/MoveModal';
import DuplicateModal from '../Sidebar/DuplicateModal';
import Cookies from 'js-cookie';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextEditorModule,
  SelectEditorModule,
  NumberEditorModule,
  PinnedRowModule,
  ColumnApiModule,
  QuickFilterModule,
  NumberFilterModule,
]);

const COLUMN_TYPES = [
  { key: 'text', label: 'Text', icon: <FontSizeOutlined /> },
  { key: 'number', label: 'Number', icon: <NumberOutlined /> },
  { key: 'assignee', label: 'Assignee', icon: <UserOutlined /> },
  { key: 'status', label: 'Status', icon: <CheckCircleOutlined /> },
  { key: 'date', label: 'Date', icon: <CalendarOutlined /> },
];

const ICON_MAP = {
  text: <FontSizeOutlined />,
  number: <NumberOutlined />,
  assignee: <UserOutlined />,
  status: <CheckCircleOutlined />,
  date: <CalendarOutlined />,
};

const defaultTableData = {
  columns: [
    { id: 'col1', name: 'Text', type: 'text', icon: 'text' },
    {
      id: 'col2',
      name: 'Status',
      type: 'status',
      icon: 'status',
      options: ['Todo', 'In Progress', 'Done'],
    },
    { id: 'col3', name: 'Assignee', type: 'assignee', icon: 'assignee' },
    { id: 'col4', name: 'Numbers', type: 'number', icon: 'number' },
  ],
  rows: [
    { id: 'row1', col1: '', col2: '', col3: '', col4: '' },
    { id: 'row2', col1: '', col2: '', col3: '', col4: '' },
    { id: 'row3', col1: '', col2: '', col3: '', col4: '' },
    { id: 'row4', col1: '', col2: '', col3: '', col4: '' },
  ],
};

const getTypeMeta = (type) => COLUMN_TYPES.find((t) => t.key === type);

const DatabaseTable = () => {
  const [tableData, setTableData] = useState(defaultTableData);

  const [isStarred, setIsStarred] = useState(false);
  const [title, setTitle] = useState('');
  const [isVersionOpen, setIsVersionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [itemType, setItemType] = useState(null);
  const [searchText, setSearchText] = useState('');

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState([]);
  const [logic, setLogic] = useState('AND');
  const [filteredRows, setFilteredRows] = useState(null);
  const [savedFilterId, setSavedFilterId] = useState(null);

  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnsList, setColumnsList] = useState([]);

  const [columnCalculations, setColumnCalculations] = useState({});

  const [selectedSortCol, setSelectedSortCol] = useState(null);
  const [sortConfig, setSortConfig] = useState(null);

  const [isOpen, setIsOpen] = useState(false);

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const [moveOpen, setMoveOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [selectedDuplicateNode, setSelectedDuplicateNode] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const gridApiRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState([]);

  const { spaceid, databaseid } = useParams();
  const roleId = Number(Cookies.get('space-role-id'));

  const isAdmin = roleId === 1;

  const isViewer = roleId === 5;

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleSearch = (value) => {
    setSearchText(value);
    gridApiRef.current?.setGridOption('quickFilterText', value);
  };

  const hiddenColumnIds = useMemo(
    () => Object.keys(columnVisibility).filter((id) => !columnVisibility[id]),
    [columnVisibility],
  );

  const updateColumnType = (colId, typeKey) => {
    const meta = getTypeMeta(typeKey);
    setTableData((prev) => ({
      ...prev,
      columns: prev.columns.map((col) =>
        col.id === colId
          ? {
              ...col,
              type: typeKey,
              icon: meta.icon,
              name: col.name || meta.label,
              options:
                typeKey === 'status' ? ['Todo', 'In Progress', 'Done'] : [],
            }
          : col,
      ),
    }));
  };

  const addColumnFromType = (typeKey) => {
    const meta = getTypeMeta(typeKey);
    const newColId = `col${Date.now()}`;
    const newColumn = {
      id: newColId,
      name: meta.label,
      type: typeKey,
      icon: typeKey,
      options: typeKey === 'status' ? ['Todo', 'In Progress', 'Done'] : [],
    };
    const updatedRows = tableData.rows.map((row) => ({
      ...row,
      [newColId]: '',
    }));
    setTableData((prev) => ({
      columns: [...prev.columns, newColumn],
      rows: updatedRows,
    }));

    setColumnVisibility((prev) => ({ ...prev, [newColId]: true }));
  };

  const renameColumn = (colId, newName) => {
    setTableData((prev) => ({
      ...prev,
      columns: prev.columns.map((col) =>
        col.id === colId ? { ...col, name: newName } : col,
      ),
    }));
  };

  const deleteColumn = (colId) => {
    setTableData((prev) => ({
      columns: prev.columns.filter((c) => c.id !== colId),
      rows: prev.rows.map((row) => {
        const r = { ...row };
        delete r[colId];
        return r;
      }),
    }));
    setColumnVisibility((prev) => {
      const v = { ...prev };
      delete v[colId];
      return v;
    });
  };

  const syncColumnWidths = (api) => {
    const gridApi = api || gridApiRef.current;
    if (!gridApi) return;

    const state = gridApi.getColumnState();
    if (!state || state.length === 0) return;

    const widths = state.map((col) => ({
      colId: col.colId,
      width: col.width,
      pinned: col.pinned,
    }));

    setColumnWidths(widths);
  };

  useEffect(() => {
    if (gridApiRef.current) {
      const timer = setTimeout(() => syncColumnWidths(), 300);
      return () => clearTimeout(timer);
    }
  }, [tableData?.columns, columnVisibility]);

  const fetchColumnCalculation = useCallback(
    async (columnId, operation) => {
      try {
        const payload = {
          space_id: spaceid,
          item_id: databaseid,
          column_id: columnId,
          operation,
        };
        const response = await httpInjectorService.columnCalculation(payload);
        const value = response?.data?.value;

        setColumnCalculations((prev) => ({
          ...prev,
          [columnId]: { operation, value },
        }));
      } catch (error) {
        console.error(error);
      }
    },
    [spaceid, databaseid],
  );

  const columnDefs = useMemo(() => {
    const baseCols = [
      {
        headerName: '#',
        width: 80,
        pinned: 'left',
        sortable: false,
        filter: false,
        valueGetter: (params) => {
          if (params?.node?.rowPinned === 'bottom') return '';
          return params.node.rowIndex + 1;
        },
        cellRenderer: (params) => {
          if (params?.node?.rowPinned === 'bottom') {
            return (
              <span
                onClick={handleAddRow}
                style={{ cursor: 'pointer', color: '#666' }}
              >
                + Add Row
              </span>
            );
          }
          return params.value;
        },
      },
    ];

    const dynamicCols = (tableData?.columns || []).map((col) => {
      const isHidden = columnVisibility[col.id] === false;

      const menuItems = [
        { key: 'count', label: 'Count' },
        { key: 'empty', label: 'Empty' },
        { key: 'nonEmpty', label: 'Non Empty' },
        { key: 'percentEmpty', label: '% Empty' },
        { key: 'percentNonEmpty', label: '% Filled' },
        ...(col.type === 'number'
          ? [
              { key: 'sum', label: 'Sum' },
              { key: 'avg', label: 'Average' },
              { key: 'min', label: 'Min' },
              { key: 'max', label: 'Max' },
            ]
          : []),
      ].map((m) => ({
        key: m.key,
        label: m.label,
        onClick: () => fetchColumnCalculation(col.id, m.key),
      }));

      let colDef = {
        headerName: col.name,
        field: col.id,
        editable: true,
        sortable: true,
        filter: true,
        resizable: true,
        colData: col,
        hide: isHidden,

        headerComponent: (params) => {
          const col = params.column.colDef.colData;
          const headerMenuItems = [
            {
              key: 'rename',
              label: (
                <div onClick={(e) => e.stopPropagation()}>
                  <Input
                    placeholder="Rename column"
                    defaultValue={col.name}
                    autoFocus
                    onPressEnter={(e) => renameColumn(col.id, e.target.value)}
                    onBlur={(e) => renameColumn(col.id, e.target.value)}
                  />
                </div>
              ),
            },
            { type: 'divider' },
            ...COLUMN_TYPES.map((type) => ({
              key: `type-${type.key}`,
              icon: type.icon,
              label: type.label,
              onClick: () => updateColumnType(col.id, type.key),
            })),
            { type: 'divider' },
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: 'Delete Column',
              danger: true,
              onClick: () => deleteColumn(col.id),
            },
          ];

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {ICON_MAP[col.icon]}
                <span>{col.name}</span>
              </div>
              <Dropdown menu={{ items: headerMenuItems }} trigger={['click']}>
                <MoreOutlined style={{ cursor: 'pointer' }} />
              </Dropdown>
            </div>
          );
        },

        cellRenderer: (params) => {
          if (params?.node?.rowPinned === 'bottom') {
            const calc = columnCalculations[col.id];

            return (
              <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  className="calc-footer-cell"
                >
                  {calc ? (
                    <span
                      style={{
                        color: '#722ed1',
                        fontSize: 16,
                        fontWeight: 500,
                      }}
                    >
                      {calc.operation} : <b>{calc.value}</b>
                    </span>
                  ) : (
                    <span
                      className="calc-btn"
                      style={{ fontSize: 12, color: '#999' }}
                    >
                      Calculate
                    </span>
                  )}
                </div>
              </Dropdown>
            );
          }

          return params.value ?? null;
        },

        valueGetter: (params) => {
          return params.data?.[col.id];
        },
      };

      if (col.type === 'number') {
        colDef.filter = 'agNumberColumnFilter';
        colDef.valueParser = (p) => Number(p.newValue);
        colDef.comparator = (a, b) => (Number(a) || 0) - (Number(b) || 0);
      } else {
        colDef.comparator = (a, b) => {
          if (!a && !b) return 0;
          if (!a) return -1;
          if (!b) return 1;
          return a.toString().localeCompare(b.toString());
        };
      }
      if (col.type === 'status') {
        colDef.cellEditor = 'agSelectCellEditor';
        colDef.cellEditorParams = { values: col.options || [] };
      }
      if (col.type === 'date') {
        colDef.cellEditor = 'agTextCellEditor';
      }

      return colDef;
    });

    const addColumnBtn = {
      headerName: '',
      field: 'add_column',
      pinned: 'right',
      width: 60,
      sortable: false,
      filter: false,
      suppressMenu: true,
      headerComponent: () => {
        const items = COLUMN_TYPES.map((type) => ({
          key: type.key,
          icon: type.icon,
          label: type.label,
          onClick: () => addColumnFromType(type.key),
        }));
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <span style={{ cursor: 'pointer', fontSize: 18 }}>+</span>
          </Dropdown>
        );
      },
      cellRenderer: (params) => {
        if (params?.node?.rowPinned === 'bottom') return null;
        return null;
      },
    };

    return [...baseCols, ...dynamicCols, addColumnBtn];
  }, [tableData, columnVisibility, columnCalculations, fetchColumnCalculation]);

  const rowData = useMemo(
    () => filteredRows || tableData?.rows,
    [filteredRows, tableData],
  );

  const handleSort = (col, direction) => {
    setSelectedSortCol(col.id);
    setSortConfig(direction);

    if (!gridApiRef.current) return;

    let sortOrder = null;

    if (direction === 'a-z' || direction === 'low-high') {
      sortOrder = 'asc';
    } else if (direction === 'z-a' || direction === 'high-low') {
      sortOrder = 'desc';
    }

    gridApiRef.current.applyColumnState({
      state: [
        {
          colId: col.id,
          sort: sortOrder,
        },
      ],
      defaultState: { sort: null },
    });
  };

  const handleClearSort = () => {
    setSortConfig(null);
    setSelectedSortCol(null);

    gridApiRef.current?.applyColumnState({
      defaultState: { sort: null },
    });
  };

  const handleAddRow = () => {
    const newRow = { id: `row${Date.now()}` };
    tableData.columns.forEach((col) => {
      newRow[col.id] = '';
    });
    setTableData((prev) => ({ ...prev, rows: [...prev.rows, newRow] }));
  };

  const HandleSaveContent = async () => {
    try {
      const payload = {
        space_id: Number(spaceid),
        item_id: Number(databaseid),
        title,
        content: {
          type: 'database',
          data: tableData,
          hiddenColumns: hiddenColumnIds,
        },
      };
      const response = await httpInjectorService.postPageContent(payload);
      if (response.status === 'success')
        toast.success('Database updated successfully');
      queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
    } catch (error) {
      console.error(error);
    }
  };

  const GetPageContent = async () => {
    setLoading(true);
    try {
      const response = await httpInjectorService.getPageContent(
        spaceid,
        databaseid,
      );
      const data = response?.data;

      const contentData = data?.content?.data;
      const isEmptyContent =
        !contentData ||
        !contentData.columns ||
        contentData.columns.length === 0;

      setTableData(isEmptyContent ? defaultTableData : contentData);
      setTitle(data?.title || 'Untitled');
      setIsStarred(data?.is_stared === 1);
      setItemType(data?.item_type);

      const savedHidden = data?.content?.hiddenColumns || [];
      const allCols = isEmptyContent
        ? defaultTableData.columns
        : contentData.columns;

      const visibilityMap = {};
      allCols.forEach((col) => {
        visibilityMap[col.id] = !savedHidden.includes(col.id);
      });
      setColumnVisibility(visibilityMap);
    } catch (error) {
      console.error(error);
      setTableData(defaultTableData);

      const visibilityMap = {};
      defaultTableData.columns.forEach((col) => {
        visibilityMap[col.id] = true;
      });
      setColumnVisibility(visibilityMap);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (spaceid && databaseid) {
      GetPageContent();
      getcolumnList();
      getSavedFilters();
    }
  }, [spaceid, databaseid]);

  const handleTitleUpdate = async (newTitle) => {
    try {
      await httpInjectorService.renameContent({
        space_id: Number(spaceid),
        item_id: Number(databaseid),
        title: newTitle,
        item_type: itemType,
      });
      queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
    } catch (err) {
      console.error(err);
    }
  };

  const getcolumnList = async () => {
    try {
      const response = await httpInjectorService.getColumnList(
        spaceid,
        databaseid,
      );
      const cols = response?.data || [];

      const finalCols = cols.length > 0 ? cols : defaultTableData.columns;
      setColumnsList(finalCols);

      setColumnVisibility((prev) => {
        const next = { ...prev };
        finalCols.forEach((col) => {
          if (!(col.id in next)) next[col.id] = true;
        });
        return next;
      });
    } catch (error) {
      console.error(error);
      setColumnsList(defaultTableData.columns);
    }
  };

  const handleFilterDatabase = async ({ filters, logic }) => {
    try {
      const visibleFilters = filters.filter(
        (f) => columnVisibility[f.columnId] !== false,
      );

      const payload = {
        space_id: spaceid,
        item_id: databaseid,
        logic,
        filters: visibleFilters.map((f) => ({
          columnId: f.columnId,
          operator: f.operation,
          value: f.value,
        })),
      };

      const response = await httpInjectorService.databaseFilters(payload);
      const updatedRows = response?.data?.content?.data?.rows || [];
      setFilteredRows(updatedRows);
    } catch (error) {
      console.error(error);
    }
  };

  const getSavedFilters = async () => {
    try {
      const res = await httpInjectorService.getSavedFilters(
        spaceid,
        databaseid,
      );
      const saved = res?.data?.[0];
      if (saved) {
        const parsed = JSON.parse(saved?.filter_value);
        setFilters(parsed.filters);
        setLogic(parsed.logic);
        setSavedFilterId(saved.id);
        handleFilterDatabase(parsed);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveFilter = async ({ filters, logic }) => {
    try {
      const visibleFilters = filters.filter(
        (f) => columnVisibility[f.columnId] !== false,
      );
      const payload = {
        space_id: spaceid,
        item_id: databaseid,
        logic,
        filters: visibleFilters.map((f) => ({
          columnId: f.columnId,
          operator: f.operation,
          value: f.value,
        })),
        filter_name: 'My Filter',
      };
      await httpInjectorService.databaseFilters(payload);
      await getSavedFilters();
    } catch (err) {
      console.error(err);
    }
  };

  const handleClearFilter = async () => {
    try {
      if (savedFilterId) {
        await httpInjectorService.deleteSavedFilters(
          spaceid,
          databaseid,
          savedFilterId,
        );
      }
      setFilters([]);
      setLogic('AND');
      setFilteredRows(null);
      setSavedFilterId(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePreviewVersion = async (versionId) => {
    try {
      const response = await httpInjectorService.getVersionDetails(
        spaceid,
        versionId,
      );

      if (response?.status !== 'success') {
        toast.error(response?.message || 'Failed to load version preview');
        return;
      }

      const versionData = Array.isArray(response.data)
        ? response.data[0]
        : response.data;

      const content = versionData?.content;

      if (!content) {
        toast.error('No content available for this version');
        return;
      }

      const parsed =
        typeof content === 'string' ? JSON.parse(content) : content;

      const dbData = parsed?.data;

      if (!dbData?.columns || !dbData?.rows) {
        toast.error('No data');
        return;
      }

      setPreviewData(dbData);
      setIsPreviewMode(true);
    } catch (err) {
      console.error('preview load error:', err);
      toast.error('Failed to load version preview');
    }
  };

  const handleExitPreview = () => {
    setPreviewData(null);
    setIsPreviewMode(false);
  };

  const handleCloseVersion = () => {
    setIsVersionOpen(false);
    //setPreviewContent(null);
  };

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const handleMove = async ({ dragId, dropId }) => {
    await httpInjectorService.movePageOrFolders({
      space_id: Number(spaceid),
      item_id: Number(databaseid),
      parent_id: dropId,
    });
    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleDuplicate = () => {
    setSelectedDuplicateNode({
      item_id: databaseid,
      item_type: 'database',
    });
    setDuplicateOpen(true);
  };

  const handleDuplicateConfirm = async ({
    original_id,
    target_space_id,
    parent_id,
    prefix,
    is_child,
  }) => {
    await httpInjectorService.duplicateContent({
      space_id: Number(target_space_id),
      item_id: Number(databaseid),
      parent_id: parent_id || null,
      startname: prefix,
      is_child: 'no',
    });

    queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
  };

  const handleStarContent = async () => {
    try {
      const payload = {
        space_id: Number(spaceid),
        item_id: Number(databaseid),
        item_type: 'database',
        action: 'star',
      };
      const response = await httpInjectorService.starShortcut(payload);
      // console.log(response);
      if (response.status === 'success') {
        toast.success(response.message, {
          autoClose: 1000,
          position: 'top-right',
        });
        setIsStarred(!isStarred);
      }
    } catch (error) {
      toast.error(error.message, {
        autoClose: 1000,
        position: 'top-right',
      });
    }
  };

  const handleDelete = async () => {
    const response = await httpInjectorService.deleteorArchivePgorFolder({
      space_id: Number(spaceid),
      item_id: databaseid,
      status_type: 'deleted',
      nested_item: 'yes',
      type: 'database',
    });
    if (response.status === 'success') {
      setDeleteModalOpen(false);
      toast.success('Item Deleted successfully');
      navigate(`${window.location.origin}/admin/confluence/space/${spaceid}`);
    }

    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleArchive = async () => {
    const response = await httpInjectorService.deleteorArchivePgorFolder({
      space_id: Number(spaceid),
      item_id: databaseid,
      status_type: 'archived',
      nested_item: 'yes',
      type: 'database',
    });
    if (response.status === 'success') {
      setDeleteModalOpen(false);
      toast.success('Item Archives successfully');
      navigate(`${window.location.origin}/admin/confluence/space/${spaceid}`);
    }

    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleExport = (format) => {
    const visibleColumns = tableData.columns.filter(
      (col) => !hiddenColumnIds.includes(col.id),
    );
    const rows = filteredRows || tableData.rows;

    if (format === 'csv') {
      exportCSV(visibleColumns, rows);
    } else if (format === 'xml') {
      exportXML(visibleColumns, rows);
    }
  };

  const exportCSV = (columns, rows) => {
    const headers = columns.map((col) => col.name).join(',');

    const csvRows = rows.map((row) =>
      columns
        .map((col) => {
          let value = row[col.id] ?? '';
          // Escape commas and quotes
          if (
            typeof value === 'string' &&
            (value.includes(',') || value.includes('"') || value.includes('\n'))
          ) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(','),
    );

    const csvContent = [headers, ...csvRows].join('\n');
    downloadFile(csvContent, 'export.csv', 'text/csv');
  };

  const exportXML = (columns, rows) => {
    const xmlRows = rows
      .map((row) => {
        const cols = columns
          .map((col) => {
            const safeTag = col.name.replace(/\s+/g, '_').replace(/[^\w]/g, '');
            const value = row[col.id] ?? '';
            return `    <${safeTag}>${value}</${safeTag}>`;
          })
          .join('\n');
        return `  <row>\n${cols}\n  </row>`;
      })
      .join('\n');

    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>\n<rows>\n${xmlRows}\n</rows>`;
    downloadFile(xmlContent, 'export.xml', 'application/xml');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const moreMenu = {
    items: [
      {
        key: 'star',
        icon: isStarred ? (
          <StarFilled style={{ color: '#faad14' }} />
        ) : (
          <StarOutlined />
        ),
        label: isStarred ? 'Unstar' : 'Star',
      },
      {
        key: 'export',
        icon: <ExportOutlined />,
        label: 'Export',
        children: [
          {
            key: 'export_csv',
            label: 'Export as CSV',
          },
          {
            key: 'export_xml',
            label: 'Export as XML',
          },
        ],
      },
      {
        key: 'version',
        icon: <ClockCircleOutlined />,
        label: 'Version History',
        onClick: () => setIsVersionOpen((p) => !p),
      },
      { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate' },
      { key: 'move', icon: <DragOutlined />, label: 'Move' },
      { key: 'delete', icon: <DeleteOutlined />, label: 'Archive and Delete' },
    ],
    onClick: ({ key }) => {
      if (key === 'export_csv') handleExport('csv');
      if (key === 'export_xml') handleExport('xml');

      if (key === 'star') handleStarContent();

      if (key === 'duplicate') handleDuplicate();

      if (key === 'move') setMoveOpen(true);

      if (key === 'delete') setDeleteModalOpen(true);
    },
  };

  if (loading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SkeletonWithLoaders size="large" />
      </div>
    );
  }

  const columnMenu = (
    <div
      style={{
        width: 240,
        maxHeight: 300,
        overflowY: 'auto',
        background: '#fff',
        border: '1px solid #eee',
        borderRadius: 8,
        padding: 10,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        Show / Hide Columns
      </div>

      {columnsList.map((col) => (
        <div
          key={col.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <span style={{ fontSize: 13 }}>{col.name}</span>
          <Switch
            size="small"
            checked={columnVisibility[col.id] !== false}
            onChange={(checked) =>
              setColumnVisibility((prev) => ({ ...prev, [col.id]: checked }))
            }
          />
        </div>
      ))}

      <div
        style={{
          marginTop: 10,
          borderTop: '1px solid #eee',
          paddingTop: 8,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{ cursor: 'pointer', fontSize: 12 }}
          onClick={() => {
            const all = {};
            columnsList.forEach((c) => (all[c.id] = true));
            setColumnVisibility(all);
          }}
        >
          Show all
        </span>
        <span
          style={{ cursor: 'pointer', fontSize: 12 }}
          onClick={() => {
            const none = {};
            columnsList.forEach((c) => (none[c.id] = false));
            setColumnVisibility(none);
          }}
        >
          Hide all
        </span>
      </div>
    </div>
  );

  const sortMenuItems = tableData?.columns
    .filter((col) => columnVisibility[col.id] !== false)
    .map((col) => {
      const isNumber = col.type === 'number';

      return {
        key: col.id,
        label: col.name,
        children: [
          ...(isNumber
            ? [
                {
                  key: `${col.id}-low-high`,
                  label: 'Low → High',
                  onClick: () => handleSort(col, 'low-high'),
                },
                {
                  key: `${col.id}-high-low`,
                  label: 'High → Low',
                  onClick: () => handleSort(col, 'high-low'),
                },
              ]
            : [
                {
                  key: `${col.id}-a-z`,
                  label: 'A → Z',
                  onClick: () => handleSort(col, 'a-z'),
                },
                {
                  key: `${col.id}-z-a`,
                  label: 'Z → A',
                  onClick: () => handleSort(col, 'z-a'),
                },
              ]),
        ],
      };
    });

  sortMenuItems?.push({
    type: 'divider',
  });
  sortMenuItems?.push({
    key: 'clear',
    label: <span style={{ color: '#ff4d4f' }}>Clear Sort</span>,
    onClick: handleClearSort,
  });

  return (
    <>
      <div className="page-wrapper">
        <div className="editor-card-header">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={(e) => handleTitleUpdate(e.target.value)}
            bordered={false}
            className="editor-title-input"
            style={{ fontSize: 20, fontWeight: 600, width: 400 }}
          />

          <div className="editor-actions">
            {!isViewer && (
              <>
                <Button
                  style={{ backgroundColor: '#722ed1', color: 'white' }}
                  onClick={HandleSaveContent}
                >
                  Save
                </Button>
                <Button icon={<LinkOutlined />} onClick={onOpen}>
                  Share
                </Button>
              </>
            )}
            {isAdmin && (
              <Dropdown menu={moreMenu} trigger={['click']}>
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            )}
          </div>
        </div>

        <div className="editor-content-wrapper">
          <div className="editor-split-container">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '20px 16px',
                  borderBottom: '1px solid #eee',
                  background: '#fff',
                }}
              >
                <Input.Search
                  placeholder="Search..."
                  style={{ width: 250 }}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  onSearch={(value) => handleSearch(value)}
                  allowClear
                  onClear={() => handleSearch('')}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                   {!isViewer && (
                    <>
                  <Button
                    icon={<FilterOutlined />}
                    style={{
                      background: '#7a3db8',
                      border: 'none',
                      color: '#fff',
                    }}
                    onClick={() => setIsFilterOpen(true)}
                  >
                    Filter {filteredRows ? `(${filteredRows.length})` : ''}
                  </Button>
                  {/* 
                  <Dropdown
                    menu={{ items: sortMenuItems }}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button
                      icon={<SortAscendingOutlined />}
                      style={{
                        background: sortConfig ? '#531dab' : '#7a3db8',
                        border: 'none',
                        color: '#fff',
                      }}
                    >
                      Sort{' '}
                      {selectedSortCol && sortConfig
                        ? `(${selectedSortCol} : ${sortConfig})`
                        : ''}
                    </Button>
                  </Dropdown> */}
                  <Dropdown
                    dropdownRender={() => columnMenu}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button
                      icon={<EyeInvisibleOutlined />}
                      style={{
                        background: '#7a3db8',
                        border: 'none',
                        color: '#fff',
                      }}
                    >
                      Hide{' '}
                      {hiddenColumnIds.length > 0
                        ? `(${hiddenColumnIds.length})`
                        : ''}
                    </Button>
                  </Dropdown>
                  </>
                      )}
                </div>
              </div>
              <div
                className="ag-theme-quartz"
                style={{ flex: 1, minHeight: 0, width: '100%' }}
              >
                {isPreviewMode && (
                  <div
                    style={{
                      background: '#fff7e6',
                      border: '1px solid #ffd591',
                      padding: '8px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 13,
                      color: '#d46b08',
                    }}
                  >
                    <span>👁️ Previewing a previous version — read only</span>
                    <Button size="small" onClick={handleExitPreview}>
                      Exit Preview
                    </Button>
                  </div>
                )}
                {(previewData || tableData)?.columns?.length > 0 && (
                  <AgGridReact
                    key={tableData?.columns?.length}
                    headerHeight={50}
                    rowData={isPreviewMode ? previewData.rows : rowData}
                    columnDefs={
                      isPreviewMode
                        ? [
                            {
                              headerName: '#',
                              width: 80,
                              pinned: 'left',
                              valueGetter: (params) => params.node.rowIndex + 1,
                            },
                            ...(previewData?.columns || []).map((col) => ({
                              headerName: col.name,
                              field: col.id,
                              sortable: true,
                              resizable: true,
                              editable: false,
                            })),
                          ]
                        : columnDefs
                    }
                    defaultColDef={{
                      sortable: true,
                    }}
                    rowModelType="clientSide"
                    animateRows
                    domLayout="normal"
                    rowBuffer={5}
                    pinnedBottomRowData={
                      isPreviewMode ? [] : [{ _isCalcRow: true }]
                    }
                    getRowHeight={(params) =>
                      params?.node?.rowPinned === 'bottom' ? 45 : 35
                    }
                    onGridReady={(params) => {
                      gridApiRef.current = params.api;
                      setTimeout(() => syncColumnWidths(params.api), 100);
                    }}
                    onColumnResized={(params) => syncColumnWidths(params.api)}
                    onColumnVisible={(params) => syncColumnWidths(params.api)}
                    onFirstDataRendered={(params) =>
                      syncColumnWidths(params.api)
                    }
                  />
                )}
              </div>
            </div>

            {(isVersionOpen || isFilterOpen) && (
              <div className="editor-right">
                {isFilterOpen ? (
                  <FilterPanel
                    filters={filters}
                    setFilters={setFilters}
                    onClose={() => setIsFilterOpen(false)}
                    columns={columnsList.filter(
                      (col) => columnVisibility[col.id] !== false,
                    )}
                    logic={logic}
                    setLogic={setLogic}
                    onApply={(data) => {
                      handleFilterDatabase(data);
                      handleSaveFilter(data);
                    }}
                    onClear={handleClearFilter}
                  />
                ) : (
                  <VersionPanel
                    typeid={databaseid}
                    spaceid={spaceid}
                    onClose={handleCloseVersion}
                    GetBoardContent={GetPageContent}
                    onPreview={handlePreviewVersion}
                    itemType={itemType}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ShareItemPermision
        isOpen={isOpen}
        onClose={onClose}
        space_id={spaceid}
        item_id={databaseid}
      />

      <MoveModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        node={{ item_id: databaseid }}
        spaceid={spaceid}
        onConfirm={handleMove}
      />

      <DuplicateModal
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        originalNode={selectedDuplicateNode}
        spaceid={spaceid}
        onConfirm={handleDuplicateConfirm}
      />

      <Modal
        open={deleteModalOpen}
        footer={null}
        onCancel={() => setDeleteModalOpen(false)}
      >
        <h3>Archive or Delete Page</h3>
        <p>
          Items in the trash can be restored or purged by a space admin. If you
          prefer, you can archive this instead.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            marginTop: '20px',
          }}
        >
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button onClick={handleArchive}>Archive</Button>
          <Button danger onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default DatabaseTable;
